<script setup lang="ts">
  import type { MaybeRef } from 'vue'
  import type { ID, Maybe } from '@/types'
  import type { CustomerCartProductListItemType } from '@/types/customer/products'

  import { computed, unref } from 'vue'

  import { useUiStore } from '@/stores/ui'

  import { useProductPrices } from '@/composables/useProductPrices'
  import {
    findByPropValue,
    formatPrice,
    getImageAlt,
    getImageUrl,
  } from '@/helpers'

  import ManagerButton from '@/components/Global/Manager/Forms/ManagerButton.vue'
  import ManagerLabel from '@/components/Global/Manager/Forms/ManagerLabel.vue'
  import ManagerTextButton from '@/components/Global/Manager/Forms/ManagerTextButton.vue'
  import CustomerRoundButton from '@/components/Global/Customer/Catalog/CustomerRoundButton.vue'
  import ManagerIcon from '@/components/Global/Manager/ManagerIcon.vue'

  const props = defineProps<{
    item: MaybeRef<Maybe<CustomerCartProductListItemType>>
    amount: number
    editable?: boolean
    dense?: boolean
    hideAmount?: boolean
  }>()
  const emit = defineEmits<{
    (e: 'change-amount', { id, amount }: { id: ID; amount?: number }): void
  }>()

  const currentItem = computed(() => unref(props.item))

  const currentId = computed(() => currentItem.value?.id)

  const src = computed(() =>
    getImageUrl(0, currentItem.value, undefined, 'product_small')
  )
  const alt = computed(() => getImageAlt(0, currentItem.value))

  const { price, hasDiscount, discountedPrice } = useProductPrices(props.item)

  const outOfStock = computed(() => !currentItem.value?.stock)

  const labelName = computed(() => {
    const labels = currentItem.value?.labels ?? []
    const value = outOfStock.value ? 'out_of_stock' : 'sale'
    const defaultName = outOfStock.value ? 'нет в наличии' : 'распродажа'

    return findByPropValue(labels, value, 'type')?.name ?? defaultName
  })
  /**
   * Проверяем по всем id в sizes, а добавляем всегда только первый из массива
   * */
  const ui = useUiStore()
  const isFavorite = computed(() => {
    if (currentId.value === undefined) return false

    const ids = currentItem.value?.sizes?.map((item) => item.id) ?? [
      currentId.value,
    ]

    return ids.some((id) => ui.getFavoriteIndex(id) !== -1)
  })

  function switchFavorite() {
    const ids =
      currentItem.value?.sizes?.map((item) => item.id) ??
      ([currentId.value] as number[])

    let deselected = false

    ids.forEach((id) => {
      if (ui.getFavoriteIndex(id) !== -1) {
        ui.switchFavorite(id)

        deselected = true
      }
    })

    if (!deselected) ui.switchFavorite(ids[0])
  }

  function changeAmount(amount?: number) {
    if (currentId.value === undefined) return

    emit('change-amount', { id: currentId.value, amount })
  }
</script>

<template>
  <div v-if="props.item" class="flex" :class="props.dense ? 'py-2' : 'py-4'">
    <div class="relative shrink-0">
      <img
        class="h-auto aspect-0.7 object-cover"
        :class="props.dense ? 'w-[87px]' : 'w-[99px] lg:w-[112px]'"
        :src="src ?? undefined"
        :alt="alt"
        loading="lazy"
      />

      <ManagerLabel
        v-if="!props.dense && (hasDiscount || outOfStock)"
        class="absolute bottom-0 left-0 !text-c2.5xs lg:!text-cxs bg-white !px-[10px] lg:!px-3 !py-1 !mr-0 !ml-1 !mb-1 lowercase"
      >
        {{ labelName }}
      </ManagerLabel>
    </div>

    <div class="pl-5 grow relative">
      <div class="flex pb-2 mb-1 h-[22px] items-baseline">
        <div class="text-c2.5xs uppercase font-bold align-text-bottom">
          {{ currentItem?.brand }}
        </div>

        <div v-if="props.editable" class="ml-auto relative h-4 w-4 -mr-[3px]">
          <ManagerButton
            :caption="''"
            color="transparent-black"
            class="h-5 w-5 absolute right-0"
            btn-classes="py-0 px-0 !border-0"
            @click.prevent="switchFavorite"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              :fill="isFavorite ? '#000000' : 'none'"
            >
              <path
                d="M4.29695 2.66675C5.81353 2.66675 7.11202 3.74755 8.00065 4.74467C8.88928 3.74755 10.1878 2.66675 11.7044 2.66675C13.3949 2.66675 14.6673 4.04987 14.6673 5.71437C14.6673 8.40569 10.5932 12.0001 8.00065 14.0001C5.40806 12.0001 1.33398 8.61449 1.33398 5.71437C1.33398 4.04987 2.60641 2.66675 4.29695 2.66675Z"
                :stroke="isFavorite ? '#000000' : '#7A7A7A'"
                stroke-width="1.2"
              />
            </svg>
          </ManagerButton>
        </div>
      </div>

      <div
        class="mb-[2px]"
        :class="
          props.dense ? 'text-c2.5xs lg:text-cxs' : 'text-csm lg:text-cbase'
        "
      >
        {{ currentItem?.name }}
      </div>

      <div class="text-c2.5xs lg:text-cxs text-mgray2 mb-1">
        {{ currentItem?.category }}
        <span v-if="currentItem?.size">({{ currentItem?.size }})</span>
      </div>

      <div
        class="py-1 mb-1 flex items-center"
        :class="props.dense ? '' : 'min-h-[26px] lg:min-h-[30px]'"
      >
        <div
          v-if="!outOfStock"
          :class="
            props.dense ? 'text-c2.5xs lg:text-cxs' : 'text-csm lg:text-cbase'
          "
        >
          {{ formatPrice(discountedPrice) }}&nbsp;&#8381;
        </div>

        <template v-if="hasDiscount && !outOfStock">
          <div
            class="ml-2 text-mgray2 text-c2.5xs lg:text-cxs !leading-[14px] line-through"
          >
            {{ formatPrice(price) }}&nbsp;&#8381;
          </div>
        </template>
      </div>

      <div
        v-if="!props.hideAmount"
        class="absolute bottom-0 flex items-center w-full pr-5"
      >
        <div v-if="props.editable" class="flex items-center w-12 shrink-0">
          <CustomerRoundButton
            btn-classes="p-0 !w-8 !h-8"
            class="!border-mbrd fix-icon"
            @click="changeAmount(-1)"
          >
            <ManagerIcon icon="minus" color="black" />
          </CustomerRoundButton>

          <div class="shrink-0 whitespace-nowrap text-csm lg:text-cbase px-3">
            {{ props.amount ?? 0 }}
          </div>

          <CustomerRoundButton
            btn-classes="!w-8 !h-8"
            class="!border-mbrd fix-icon"
            @click="changeAmount()"
          >
            <ManagerIcon icon="plus-small" color="black" />
          </CustomerRoundButton>
        </div>

        <div
          v-if="!props.editable"
          class="shrink-0 whitespace-nowrap text-c2.5xs lg:text-cxs text-mgray2"
        >
          {{ props.amount }}&nbsp;шт
        </div>

        <div v-if="props.editable" class="ml-auto">
          <ManagerTextButton
            caption="Удалить"
            caption-classes="text-c2.5xs lg:text-cxs text-mgray2 hover:text-mblack"
            class="h-[36px] ml-1"
            @click="changeAmount(0)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .fix-icon :deep(.icon) {
    margin-right: 0 !important;
  }
</style>
